import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import UserFicheSheet from "../users/UserFicheSheet";
import { useUserSheet } from "../../contexts/UserSheetContext";
import { useAuth } from "../../App";
import { useTheme } from "../../contexts/ThemeContext";
import axios from "axios";
import { API } from "../../App";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  Clock,
  Users,
  FileText,
  Bell,
  User,
  LogOut,
  ChevronRight,
  ChevronDown,
  Building2,
  Sun,
  Moon,
  TrendingUp,
  Receipt,
  ClipboardList,
  GitBranch,
  Shield,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

const MainLayout = () => {
  const { user, logout } = useAuth();
  const { openUserId, closeSheet, openSheet, allUsers, fetchUsers } = useUserSheet();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const getRoleBadge = (role) => {
    const roleLabels = {
      admin: "Admin",
      hr: "RH",
      manager: "Manager",
      collaborateur: "Collaborateur",
      employee: "Collaborateur",
      comptabilité: "Comptabilité",
    };
    const roleColors = {
      admin: "bg-purple-600 text-white",
      hr: "bg-[#E07A5F] text-white",
      manager: "bg-[#1A4D2E] text-white",
      collaborateur: "bg-[#E8F5E9] text-[#1A4D2E]",
      employee: "bg-[#E8F5E9] text-[#1A4D2E]",
      comptabilité: "bg-blue-600 text-white",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColors[role] || roleColors.collaborateur}`}>
        {roleLabels[role] || role}
      </span>
    );
  };

  // Items principaux (sidebar + mobile bottom nav)
  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord", mobileLabel: "Accueil" },
    { to: "/calendar", icon: CalendarDays, label: "Calendrier", mobileLabel: "Calendrier" },
    { to: "/leaves", icon: Calendar, label: "Congés", mobileLabel: "Congés" },
    { to: "/absences", icon: Clock, label: "Absences", mobileLabel: "Absences" },
    { to: "/payslips", icon: FileText, label: "Bulletins de paie", mobileLabel: "Bulletins" },
    { to: "/ndf", icon: Receipt, label: "Notes de Frais", mobileLabel: "NDF" },
  ];

  const showCollabGroup = ["admin", "hr", "manager", "comptabilité"].includes(user?.role);

  // Sous-items du groupe Collaborateurs (sidebar + panel mobile)
  const collabSubItems = [
    { to: "/users", icon: Users, label: "Annuaire" },
    { to: "/trombinoscope", icon: GitBranch, label: "Organigramme" },
    ...(["admin", "hr"].includes(user?.role) ? [
      { to: "/nao", icon: TrendingUp, label: "NAO / Rémunération" },
      { to: "/onboarding", icon: ClipboardList, label: "Onboarding" },
    ] : []),
  ];

  const collabRoutes = ["/users", "/nao", "/onboarding", "/trombinoscope", "/audit"];
  const isCollabActive = collabRoutes.some(r => location.pathname.startsWith(r));
  const [collabOpen, setCollabOpen] = useState(isCollabActive);
  const [collabMobileOpen, setCollabMobileOpen] = useState(false);

  // Auto-ouvre le groupe quand on navigue vers une sous-route
  useEffect(() => {
    if (isCollabActive) setCollabOpen(true);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const NavItem = ({ to, icon: Icon, label }) => {
    const isActive = location.pathname === to;
    return (
      <NavLink
        to={to}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group
          ${isActive
            ? "bg-[#E8F5E9] dark:bg-[#0D1E3A] text-[#1A4D2E] dark:text-[#4B8BF4] font-medium shadow-sm"
            : "text-[#4B5563] dark:text-[#7EA8CE] hover:bg-gray-100 dark:hover:bg-[#112240] hover:text-[#1A4D2E] dark:hover:text-[#4B8BF4]"
          }`}
        data-testid={`nav-${to.replace("/", "")}`}
      >
        <Icon className={`w-5 h-5 transition-transform duration-300 ${isActive ? "scale-110" : "group-hover:scale-110"}`} />
        <span>{label}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </NavLink>
    );
  };

  const NavSubItem = ({ to, icon: Icon, label }) => {
    const isActive = location.pathname === to;
    return (
      <NavLink
        to={to}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-200
          ${isActive
            ? "bg-[#E8F5E9] dark:bg-[#0D1E3A] text-[#1A4D2E] dark:text-[#4B8BF4] font-medium"
            : "text-[#6B7280] dark:text-[#7EA8CE] hover:bg-gray-50 dark:hover:bg-[#112240] hover:text-[#1A4D2E] dark:hover:text-[#4B8BF4]"
          }`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span>{label}</span>
        {isActive && <ChevronRight className="w-3 h-3 ml-auto" />}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] dark:bg-[#060E1C] noise-bg transition-colors duration-300">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass dark:bg-[#0B1628]/90 border-b border-gray-200 dark:border-[#1A3050]">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A4D2E] dark:text-[#4B8BF4]">
            WorkPulse
          </h1>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="dark:text-[#E8F0FA] dark:hover:bg-[#112240]"
              data-testid="mobile-theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/notifications")}
              className="relative dark:text-[#E8F0FA] dark:hover:bg-[#112240]"
              data-testid="mobile-notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E07A5F] text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-1 focus:outline-none">
                  <Avatar className="w-8 h-8 border-2 border-[#1A4D2E]/20 dark:border-[#4B8BF4]/30">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="bg-[#1A4D2E] dark:bg-[#1A3A6A] text-white text-xs font-semibold">
                      {getInitials(user?.first_name, user?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 dark:bg-[#0B1628] dark:border-[#1A3050]">
                <div className="px-3 py-2 border-b border-gray-100 dark:border-[#1A3050]">
                  <p className="text-sm font-medium text-[#111827] dark:text-[#D6E4F5] truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] truncate">{user?.email}</p>
                </div>
                <DropdownMenuItem
                  onClick={() => navigate("/profile")}
                  className="gap-2 cursor-pointer dark:text-[#D6E4F5] dark:focus:bg-[#112240]"
                >
                  <User className="w-4 h-4" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuSeparator className="dark:bg-[#1A3050]" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="gap-2 cursor-pointer text-red-600 dark:text-[#F87171] focus:text-red-600 dark:focus:bg-red-900/20"
                >
                  <LogOut className="w-4 h-4" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 w-72 bg-white dark:bg-[#071120] border-r border-gray-200 dark:border-[#1A3050] -translate-x-full lg:translate-x-0 transition-transform duration-300 ease-out">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-100 dark:border-[#1A3050] flex items-center justify-between">
            <div>
              <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A4D2E] dark:text-[#4B8BF4]">
                WorkPulse
              </h1>
              <p className="text-sm text-[#9CA3AF] dark:text-[#7EA8CE] mt-1">Gestion RH simplifiée</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="hidden lg:flex dark:text-[#E8F0FA] dark:hover:bg-[#112240]"
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>

          {/* User Info */}
          <div className="p-4 mx-4 mt-4 rounded-xl bg-gradient-to-br from-[#1A4D2E] to-[#143D24] dark:from-[#1A4D2E] dark:to-[#143D24] text-white">
            <div className="flex items-center gap-3">
              <Avatar className="w-12 h-12 border-2 border-white/20">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-white/20 text-white font-semibold">
                  {getInitials(user?.first_name, user?.last_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-white/70 truncate">{user?.position}</p>
                <p className="text-xs text-white/60 truncate mt-0.5">{user?.email}</p>
                {user?.hire_date && (
                  <p className="text-xs text-white/50 mt-0.5">
                    Depuis {format(new Date(user.hire_date), "MMM yyyy", { locale: fr })}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              {getRoleBadge(user?.role)}
              <span className="text-xs text-white/60">{user?.department}</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}

            {/* Groupe Collaborateurs */}
            {showCollabGroup && (
              <div>
                <button
                  onClick={() => setCollabOpen(!collabOpen)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300
                    ${isCollabActive
                      ? "text-[#1A4D2E] dark:text-[#4B8BF4] font-medium"
                      : "text-[#4B5563] dark:text-[#7EA8CE] hover:bg-gray-100 dark:hover:bg-[#112240] hover:text-[#1A4D2E] dark:hover:text-[#4B8BF4]"
                    }`}
                >
                  <Building2 className="w-5 h-5 shrink-0" />
                  <span className="flex-1 text-left">Collaborateurs</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${collabOpen ? "rotate-180" : ""}`} />
                </button>
                {collabOpen && (
                  <div className="mt-1 ml-3 pl-3 border-l-2 border-gray-100 dark:border-[#1A3050] space-y-0.5">
                    {collabSubItems.map((item) => (
                      <NavSubItem key={item.to} {...item} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Audit — admin only */}
            {user?.role === "admin" && (
              <NavItem to="/audit" icon={Shield} label="Journal d'audit" />
            )}
          </nav>

          {/* Bottom Actions */}
          <div className="p-4 border-t border-gray-100 dark:border-[#1A3050] space-y-2">
            <NavLink
              to="/notifications"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#4B5563] dark:text-[#7EA8CE] hover:bg-gray-100 dark:hover:bg-[#112240] transition-all"
              data-testid="nav-notifications"
            >
              <Bell className="w-5 h-5" />
              <span>Notifications</span>
              {unreadCount > 0 && (
                <Badge className="ml-auto bg-[#E07A5F] hover:bg-[#D06A4F]">
                  {unreadCount}
                </Badge>
              )}
            </NavLink>

            <NavLink
              to="/profile"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#4B5563] dark:text-[#7EA8CE] hover:bg-gray-100 dark:hover:bg-[#112240] transition-all"
              data-testid="nav-profile"
            >
              <User className="w-5 h-5" />
              <span>Mon profil</span>
            </NavLink>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 dark:text-[#F87171] hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-16 lg:pt-0 pb-20 lg:pb-0">
        <div className="p-4 md:p-8 lg:p-12">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-[#070F1D] border-t border-gray-200 dark:border-[#1A3050] mobile-nav">
        <div className="flex justify-around py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all
                  ${isActive ? "text-[#1A4D2E] dark:text-[#4B8BF4]" : "text-[#9CA3AF] dark:text-[#3D607E]"}`}
                data-testid={`mobile-nav-${item.to.replace("/", "")}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? "scale-110" : ""}`} />
                <span className="text-[10px] leading-tight text-center">{item.mobileLabel || item.label}</span>
              </NavLink>
            );
          })}

          {/* Collabs — ouvre le panel sous-menu */}
          {showCollabGroup && (
            <button
              onClick={() => setCollabMobileOpen(true)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all
                ${isCollabActive ? "text-[#1A4D2E] dark:text-[#4B8BF4]" : "text-[#9CA3AF] dark:text-[#3D607E]"}`}
              data-testid="mobile-nav-collabs"
            >
              <Building2 className={`w-5 h-5 ${isCollabActive ? "scale-110" : ""}`} />
              <span className="text-[10px] leading-tight text-center">Collabs</span>
            </button>
          )}
        </div>
      </nav>

      {/* Mobile — panel Collaborateurs (glisse depuis le bas) */}
      {/* Fiche collaborateur — globale, accessible depuis tout le site */}
      <UserFicheSheet
        userId={openUserId}
        open={!!openUserId}
        onClose={closeSheet}
        allUsers={allUsers}
        onRefresh={fetchUsers}
        onOpenUser={openSheet}
      />

      {collabMobileOpen && (
        <div
          className="fixed inset-0 z-[60] lg:hidden"
          onClick={() => setCollabMobileOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
          <div
            className="absolute bottom-[65px] left-0 right-0 bg-white dark:bg-[#0B1628] border-t border-gray-200 dark:border-[#1A3050] rounded-t-2xl overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="pt-3 pb-2 flex justify-center">
              <div className="w-10 h-1 bg-gray-200 dark:bg-[#1A3050] rounded-full" />
            </div>
            <div className="px-4 pb-2 flex items-center gap-2 border-b border-gray-100 dark:border-[#1A3050]">
              <Building2 className="w-4 h-4 text-[#1A4D2E] dark:text-[#4B8BF4]" />
              <p className="font-semibold text-sm text-[#111827] dark:text-[#D6E4F5] pb-2 pt-1">Collaborateurs</p>
            </div>
            <div className="p-3 space-y-1">
              {collabSubItems.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => { setCollabMobileOpen(false); window.scrollTo({ top: 0, behavior: "instant" }); }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                      ${isActive
                        ? "bg-[#E8F5E9] dark:bg-[#0D1E3A] text-[#1A4D2E] dark:text-[#4B8BF4] font-medium"
                        : "text-[#4B5563] dark:text-[#7EA8CE] hover:bg-gray-50 dark:hover:bg-[#112240]"
                      }`}
                  >
                    <item.icon className="w-5 h-5 shrink-0" />
                    <span>{item.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </NavLink>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;
